import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import Logo from "@/components/Logo";
import { useI18n } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renderText = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") ? (
      <strong key={i} className="font-medium">{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );

function getSessionId() {
  let id = localStorage.getItem("my-chat-session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("my-chat-session", id);
  }
  return id;
}

export default function ChatWidget() {
  const { t, lang } = useI18n();
  const c = t.chat;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(getSessionId());
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: c.welcome }]);
    }
  }, [open, c.welcome, messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text, lang }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: c.error };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        type="button"
        data-testid="chat-open-button"
        onClick={() => setOpen((v) => !v)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-6 left-6 z-[60] flex items-center gap-2 bg-pine px-4 py-3 text-cream shadow-xl shadow-pine/25 transition-colors duration-300 hover:bg-copper"
        aria-label={c.openLabel}
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        <span className="hidden font-mono text-[10px] uppercase tracking-[0.2em] sm:block">
          {c.openLabel}
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-24 left-4 z-[60] flex h-[480px] w-[calc(100vw-2rem)] max-w-sm flex-col border border-copper/25 bg-paper shadow-2xl shadow-pine/25 sm:left-6"
          >
            <div className="flex items-center gap-3 border-b border-pine/10 bg-pine px-4 py-3">
              <Logo className="h-8 w-8" />
              <div>
                <div className="font-serif text-base leading-tight text-cream">{c.title}</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-cream/60">{c.subtitle}</div>
              </div>
            </div>

            <div ref={listRef} data-testid="chat-messages" className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm font-light leading-relaxed ${
                    m.role === "user"
                      ? "self-end bg-pine text-cream"
                      : "self-start border border-pine/10 bg-cream text-bark"
                  }`}
                >
                  {renderText(m.text)}
                  {m.role === "assistant" && streaming && i === messages.length - 1 && (
                    <span className="ml-1 inline-block h-3 w-1.5 animate-pulse bg-copper align-middle" />
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 border-t border-pine/10 p-3">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={c.placeholder}
                className="flex-1 border border-pine/15 bg-white px-3 py-2.5 text-sm font-light text-bark placeholder:text-sand/60 focus:border-copper focus:outline-none"
              />
              <button
                type="button"
                data-testid="chat-send-button"
                onClick={send}
                disabled={streaming || !input.trim()}
                aria-label={c.openLabel}
                className="flex h-10 w-10 items-center justify-center bg-copper text-paper transition-colors duration-300 hover:bg-pine disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
